import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  WritableSignal,
  signal,
  computed,
  effect,
  untracked,
} from '@angular/core';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import { LazyLoadEvent } from 'primeng/api';
import {
  ActiveFilterService,
  MetricsGridConfig,
  ColumnDefinitionService,
  BreadcrumbService,
  ClientProfileService,
  FormatService,
  GridTableNgComponent,
  GridSortingService,
} from './../core';
import { HeaderService } from './../header';
import { DownloadsService } from './../downloads';
import { RentalAssetService } from './rental-asset.service';
import { PageOptionsNg, SortOptionsNg, SortOptions } from '../models';
import { AuthenticationService } from '../core/authentication/authentication.service';
import { Observable, BehaviorSubject } from 'rxjs';
import { SavedViewsService } from '@/app/modules/saved-views/saved-views.service';
import { TranslateService } from '@ngx-translate/core';
import { createGridSerializer } from '@/app/modules/saved-views/saved-views-serializer';
import { savedViewDeserializer } from '@/app/rental-assets/utils';
import { FeatureFlagService } from '@/app/core/feature-flags/feature-flag.service';

@Component({
  templateUrl: 'rental-assets.component.html',
  styleUrls: ['rental-asset.component.scss'],
  standalone: false,
})
export class RentalAssetsComponent implements OnInit, OnDestroy {
  loading = true;
  data: any;
  gridConfig: WritableSignal<MetricsGridConfig> = signal(null);
  frozenRows: any;
  paging = new PageOptionsNg();
  sorting: SortOptionsNg;
  totalCount = 0;
  frozenWidth: string;
  gridName: string = 'RENTALASSETS_GRID';
  private clientProfileServiceSubscription: Subscription;
  private subscriptions: Array<Subscription> = [];
  private selectedAsset: string;
  public txAttributes: WritableSignal<any> = signal(null);
  public maxNumDaysInStatus = computed(() => {
    const attributes = this.txAttributes();
    return attributes ? attributes.MaxNumDaysInStatus : null;
  });
  @ViewChild('grid') grid: GridTableNgComponent;
  constructor(
    private authenticationService: AuthenticationService,
    private filterService: ActiveFilterService,
    private columnService: ColumnDefinitionService,
    private breadcrumbService: BreadcrumbService,
    private headerService: HeaderService,
    private downloadsService: DownloadsService,
    private rentalAssetService: RentalAssetService,
    private clientProfileService: ClientProfileService,
    private formatService: FormatService,
    private gridSortingService: GridSortingService,
    private savedViewsService: SavedViewsService,
    public translateService: TranslateService,
    protected featureFlagService: FeatureFlagService
  ) {
    effect(() => {
      const areSavedViewsEnabled =
        this.featureFlagService.isActive('flagSavedViews');
      if (!areSavedViewsEnabled) {
        return;
      }
      const selectedView = this.savedViewsService.getSelectedView();
      const viewName = selectedView?.name ?? null;

      // We don't want the effect to re-run when lastAppliedViewName changes
      const lastApplied =
        this.savedViewsService.previouslySelectedViewsStack()[0] ?? null;
      if (viewName === lastApplied) {
        return;
      }

      // Use untracked to read gridConfig
      const hasGridConfig = untracked(() => !!this.gridConfig());

      if (selectedView && hasGridConfig) {
        // Call handler in untracked context to avoid tracking any signal reads inside
        untracked(async () => {
          const currentConfig = this.gridConfig();

          // Define a callback to extract visible column names for logging
          const getVisibleColumns = (config: MetricsGridConfig) => {
            return (
              config?.groups
                ?.filter((g) => g.visible)
                .map((g) => g.columnSelectorTitle) || []
            );
          };

          const newConfig = await this.savedViewsService.handleSavedViewChange(
            selectedView,
            currentConfig,
            savedViewDeserializer,
            getVisibleColumns
          );

          // Update the signal - this will automatically trigger change detection
          this.gridConfig.set(newConfig.config);

          // Signal that the full saved view application is complete
          this.savedViewsService.notifyViewApplied();
        });
      }
    });
  }

  ngOnInit() {
    const areSavedViewsEnabled =
      this.featureFlagService.isActive('flagSavedViews');
    if (areSavedViewsEnabled) {
      this.savedViewsService.initGrid(this.gridName, this.getSerializer());
    }
    this.gridSortingService.setGridName(this.gridName);
    this.sorting = this.gridSortingService.getSortOptionsNg();

    this.loading = true;
    this.breadcrumbService.update([
      { title: 'main.tabs.equipment.rental_assets.all', class: 'active' },
    ]);
    this.configureGrid().subscribe(async (isconfigured) => {
      const areSavedViewsEnabled =
        this.featureFlagService.isActive('flagSavedViews');
      if (areSavedViewsEnabled) {
        await this.savedViewsService.load();
      }
      if (isconfigured === true) {
        this.load();
        this.subscriptions.push(
          this.filterService.filterChange.subscribe(() => {
            // If saved views is currently applying a view or its filters, skip —
            // the saved view effect will handle load.
            if (this.savedViewsService.isApplying()) {
              return;
            }
            this.paging.page = 1;
            if (this.grid) {
              this.grid.resetPaginator();
            }
            this.load();
          })
        );
        this.subscriptions.push(
          this.authenticationService.userInfoView.subscribe((userInfo) => {
            if (this.gridConfig()) {
              const config = this.gridConfig();
              config.enableExcelExport = userInfo.HasClientAccessToExportData;
              this.gridConfig.set(config);
            }
          })
        );
      }
    });
  }

  ngOnDestroy() {
    while (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.pop().unsubscribe();
    }
  }

  load = () => {
    this.loading = true;

    this.rentalAssetService
      .getProductTypeEquipment(
        this.paging,
        this.getSorting(),
        this.selectedAsset
      )
      .subscribe((x) => {
        this.loading = false;

        if (x.Items && x.Items.length > 0) {
          this.data = x.Items;
          const config = this.gridConfig();
          config.lastUpdated = x.Items[0].LastUpdatedDate || null;
          this.gridConfig.set(config);
        } else {
          this.data = [];
        }

        this.totalCount = x.TotalCount;
      });
  };

  changePage = (event: any) => {
    if (event.rows !== this.paging.pageSize) {
      this.paging.pageSize = event.rows;
    }

    this.paging.page = event.first < 1 ? 1 : event.first / event.rows + 1;
    this.load();
  };

  changeLazyLoad = (event: LazyLoadEvent) => {
    if (
      this.sorting.sortField === event.sortField &&
      this.sorting.sortOrder === event.sortOrder
    ) {
      return;
    }

    this.gridSortingService.setSortOption(
      event.sortField,
      event.sortOrder === -1
    );

    this.sorting.sortField =
      event.sortField || this.gridSortingService.defaultSortColumn;
    this.sorting.sortOrder = event.sortOrder || -1;

    this.load();
  };

  exportExcel = (args: any) => {
    this.loading = true;
    const translatedConfig = this.gridConfig().cloneAndTranslate((text) =>
      this.formatService.translateAndFormat(text, false)
    );
    this.subscriptions.push(
      this.rentalAssetService
        .getProductTypeEquipmentDownload(
          translatedConfig,
          this.getSorting(),
          this.selectedAsset
        )
        .subscribe((blob) => {
          this.loading = false;
          this.downloadsService.saveExcelBlob(blob);
        })
    );
  };

  private configureGrid = (): Observable<boolean> => {
    const result = new BehaviorSubject<boolean>(false);

    this.clientProfileServiceSubscription = this.clientProfileService
      .getClientAttributes()
      .subscribe((attributes) => {
        this.txAttributes.set(attributes);
        if (this.clientProfileServiceSubscription) {
          this.clientProfileServiceSubscription.unsubscribe();
        }

        const linkDsl = (v, r) => {
          const sorting = this.getSorting();
          const params = { sortOn: sorting.sortOn, desc: sorting.descending };
          return ['.', r.RouseEquipmentID, params];
        };

        let calculatedEquipmentLength = attributes.MaxEquipmentLen * 8;
        calculatedEquipmentLength =
          calculatedEquipmentLength < 120 ? 120 : calculatedEquipmentLength;

        let calculatedProductTypeLength = attributes.MaxProdTypeLen * 7.25;
        calculatedProductTypeLength =
          calculatedProductTypeLength < 175 ? 175 : calculatedProductTypeLength;

        this.frozenWidth = `${calculatedEquipmentLength}px`;

        const config = new MetricsGridConfig(
          this.columnService.RentalAssetsColumnGroups(
            linkDsl,
            calculatedEquipmentLength,
            calculatedProductTypeLength,
            attributes.MaxNumDaysInStatus
          ),
          null,
          true
        );
        this.gridConfig.set(config);
        result.next(true);
      });

    return result.asObservable();
  };

  private getSorting = (): SortOptions => {
    this.sorting.sortField = this.gridSortingService.getSortFieldOrDefault();
    this.sorting.sortOrder =
      this.gridSortingService.getSortDirectionOrDefault();
    return new SortOptions(
      this.sorting.sortField,
      !(this.sorting.sortOrder === 1)
    );
  };

  private getSerializer = createGridSerializer({
    gridName: this.gridName,
    gridConfig: this.gridConfig,
    gridSortingService: this.gridSortingService,
  });
}
